import "./About.scss";

import { useTranslation } from "react-i18next";
import {
  BiLogoGoLang,
  BiLogoPostgresql,
  BiLogoReact,
  BiLogoTypescript,
} from "react-icons/bi";
import { FaDocker, FaEthereum, FaJava } from "react-icons/fa";
import { RiNextjsFill } from "react-icons/ri";
import { SiGrafana, SiPrometheus, SiSharp, SiSolidity, SiVite } from "react-icons/si";
import { TbBrandNodejs } from "react-icons/tb";

import GridBackground from "../../../components/GridBackground/GridBackground";
import Section from "../../../components/Section/Section";
import SectionHeader from "../../../components/SectionHeader/SectionHeader";
import FeatureItem from "./FeatureItem/FeatureItem";

const FEATURE_ICONS = [
  <svg viewBox="0 0 36 36" fill="none">
    <path
      d="M18 4L32 11V25L18 32L4 25V11L18 4Z"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path
      d="M12 18l4 4 8-8"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>,
  <svg viewBox="0 0 36 36" fill="none">
    <circle cx="18" cy="18" r="12" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M18 10V18L23 21"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>,
  <svg viewBox="0 0 36 36" fill="none">
    <path
      d="M6 18h6l4-10 4 20 4-10h6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>,
];

const STACK_ITEMS = [
  { name: "React", icon: <BiLogoReact />, color: "#61DAFB" },
  { name: "Next.js", icon: <RiNextjsFill />, color: "#ffffff" },
  { name: "TypeScript", icon: <BiLogoTypescript />, color: "#3178C6" },
  { name: "Vite", icon: <SiVite />, color: "#646CFF" },
  { name: "Node.js", icon: <TbBrandNodejs />, color: "#68A063" },
  { name: "Go", icon: <BiLogoGoLang />, color: "#00ADD8" },
  { name: "Solidity", icon: <SiSolidity />, color: "#a0a0b8" },
  { name: "Web3", icon: <FaEthereum />, color: "#8C8CFF" },
  { name: "PostgreSQL", icon: <BiLogoPostgresql />, color: "#336791" },
  { name: "Docker", icon: <FaDocker />, color: "#2496ED" },
  { name: "Java", icon: <FaJava />, color: "#f89820" },
  { name: "C#", icon: <SiSharp />, color: "#9B4F96" },
  { name: "Prometheus", icon: <SiPrometheus />, color: "#E6522C" },
  { name: "Grafana", icon: <SiGrafana />, color: "#F46800" },
];

const About = () => {
  const { t } = useTranslation();

  const features = t("about.features", { returnObjects: true }) as Array<{
    title: string;
    description: string;
  }>;

  return (
    <Section id="about" className="about">
      <GridBackground />
      <div className="aboutText">
        <SectionHeader
          tag={t("about.tag")}
          title={t("about.title")}
          subtitle={t("about.subtitle")}
        />
        <div className="feats">
          {features.map((feature, index) => (
            <FeatureItem
              key={index}
              icon={FEATURE_ICONS[index]}
              title={feature.title}
              description={feature.description}
            />
          ))}
        </div>
      </div>
      <div className="aboutVis fi">
        <svg className="connLines" aria-hidden="true">
          <line x1="110" y1="220" x2="110" y2="310" />
          <line x1="220" y1="110" x2="270" y2="110" />
          <line x1="270" y1="110" x2="270" y2="240" />
          <circle cx="110" cy="220" r="3" />
          <circle cx="110" cy="310" r="3" />
          <circle cx="270" cy="110" r="3" />
          <circle cx="270" cy="240" r="3" />
        </svg>

        <div className="abox abox1">
          <svg width="150" height="150" viewBox="0 0 160 160" fill="none">
            <g className="accentStroke" strokeWidth=".8" opacity=".35">
              <polygon points="40,10 60,10 70,27 60,44 40,44 30,27" />
              <polygon points="80,10 100,10 110,27 100,44 80,44 70,27" />
              <polygon points="120,10 140,10 150,27 140,44 120,44 110,27" />
              <polygon points="20,44 40,44 50,61 40,78 20,78 10,61" />
              <polygon points="60,44 80,44 90,61 80,78 60,78 50,61" />
              <polygon points="100,44 120,44 130,61 120,78 100,78 90,61" />
              <polygon points="40,78 60,78 70,95 60,112 40,112 30,95" />
              <polygon points="80,78 100,78 110,95 100,112 80,112 70,95" />
              <polygon points="120,78 140,78 150,95 140,112 120,112 110,95" />
            </g>
            <g className="accentFill" opacity=".7">
              <circle cx="70" cy="27" r="2.5" />
              <circle cx="90" cy="61" r="2.5" />
              <circle cx="70" cy="95" r="2.5" />
              <circle cx="110" cy="27" r="1.8" />
              <circle cx="50" cy="61" r="1.8" />
            </g>
          </svg>
        </div>

        <div className="abox abox3">
          <svg width="36" height="36" viewBox="0 0 40 40" fill="none">
            <path
              d="M20 4L36 12V28L20 36L4 28V12L20 4Z"
              className="accentStroke"
              strokeWidth="1.5"
            />
            <path
              d="M20 4V36M4 12L36 28M36 12L4 28"
              className="accentStroke"
              strokeWidth=".7"
              opacity=".35"
            />
          </svg>
        </div>

        <div className="abox abox4">
          <div className="terminalBar">
            <span className="termDot" />
            <span className="termDot" />
            <span className="termDot" />
          </div>
          <div className="terminalBody">
            <div className="termLine">
              <span className="termPrompt">$</span>
              <span className="termCmd">git push origin main</span>
            </div>
            <div className="termLine termOk">
              <span className="termCheck">✔</span> build (8s)
            </div>
            <div className="termLine termOk">
              <span className="termCheck">✔</span> tests ×24
            </div>
            <div className="termLine termOk">
              <span className="termCheck">✔</span> deploy
            </div>
            <div className="termLine termLive">
              <span className="termPulse" />
              live on prod
            </div>
          </div>
        </div>

        <div className="abox abox2">
          <div className="stackLabel">{t("about.stack_label")}</div>
          <div className="stackGrid">
            {STACK_ITEMS.map(({ name, icon, color }) => (
              <div key={name} className="stackItem">
                <span className="stackItemIcon" style={{ color }}>
                  {icon}
                </span>
                <span className="stackItemName">{name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
};

export default About;
